<template>
  <div class="person-filter-content">
    <quick-filter-panel
      ref="filterPanel"
      :fields="filterFields"
      :values="filterValues"
      title="Filter People"
      :show-header="false"
      @update="handleFilterUpdate"
      @clear="resetFilters"
    />
  </div>
</template>

<script>
import QuickFilterPanel from '@/tools/components/filter/QuickFilterPanel.vue'

/**
 * PersonFilterContent Component
 *
 * Filter controls for Person view, using QuickFilterPanel for unified UX.
 * Embedded in ToolsDrawer's filter-controls slot.
 */
export default {
  name: 'PersonFilterContent',
  components: {
    QuickFilterPanel,
  },

  props: {
    selectedTypeFilter: {
      type: String,
      default: null,
    },
    selectedAccessFilter: {
      type: String,
      default: null,
    },
    selectedConfigFilter: {
      type: String,
      default: null,
    },
    selectedAdminFilter: {
      type: String,
      default: null,
    },
    typeFilterOptions: {
      type: Array,
      default: () => [],
    },
    accessFilterOptions: {
      type: Array,
      default: () => [],
    },
    configFilterOptions: {
      type: Array,
      default: () => [],
    },
    adminFilterOptions: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    /**
     * Filter field configuration for QuickFilterPanel
     */
    filterFields() {
      return [
        {
          key: 'type',
          label: 'Type',
          icon: ['fas', 'user-tag'],
          items: this.typeFilterOptions,
          multiple: false,
        },
        {
          key: 'access',
          label: 'Access',
          icon: ['fas', 'key'],
          items: this.accessFilterOptions,
          multiple: false,
        },
        {
          key: 'config',
          label: 'Config Status',
          icon: ['fas', 'cog'],
          items: this.configFilterOptions,
          multiple: false,
        },
        {
          key: 'admin',
          label: 'Admin Status',
          icon: ['fas', 'user-shield'],
          items: this.adminFilterOptions,
          multiple: false,
        },
      ]
    },

    /**
     * Current filter values mapped to QuickFilterPanel format
     */
    filterValues() {
      return {
        type: this.selectedTypeFilter,
        access: this.selectedAccessFilter,
        config: this.selectedConfigFilter,
        admin: this.selectedAdminFilter,
      }
    },
  },

  methods: {
    /**
     * Handle filter updates from QuickFilterPanel
     * QuickFilterPanel emits the full values object, not (key, value) pairs
     */
    handleFilterUpdate(newValues) {
      const eventMap = {
        type: 'update:selectedTypeFilter',
        access: 'update:selectedAccessFilter',
        config: 'update:selectedConfigFilter',
        admin: 'update:selectedAdminFilter',
      }

      // Emit changes for each filter key
      Object.entries(newValues || {}).forEach(([key, value]) => {
        if (eventMap[key]) {
          this.$emit(eventMap[key], value)
        }
      })
    },

    /**
     * Reset all filters to their default values
     * Called by parent via ref when ToolsDrawer Reset button is clicked
     */
    resetFilters() {
      this.$emit('update:selectedTypeFilter', null)
      this.$emit('update:selectedAccessFilter', null)
      this.$emit('update:selectedConfigFilter', null)
      this.$emit('update:selectedAdminFilter', null)
    },

    /**
     * Get current filter values
     * Called by parent via ref to get filter state
     */
    getFilters() {
      return {
        selectedTypeFilter: this.selectedTypeFilter,
        selectedAccessFilter: this.selectedAccessFilter,
        selectedConfigFilter: this.selectedConfigFilter,
        selectedAdminFilter: this.selectedAdminFilter,
      }
    },
  },
}
</script>

<style scoped lang="scss">
.person-filter-content {
  padding: 8px 0;
}
</style>
